import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Box, Button, IconButton, TextField, Tooltip } from "@mui/material";
import { useGetAllRenewableEnergyPurchases } from "hooks/api/renewable-energy-purchases";
import {
  deletePurchase,
  exportRenewableEnergyPurchasesData,
  RenewableEnergyPurchasesDTO,
  RENEWABLE_ENERGY_PURCHASES_API_PATHS,
} from "api";
import { EsgDeleteIcon, EsgRecUploaded, EsgSearchIcon, PlusIcon } from "assets";
import { useUserRole } from "recoils";
import { COLORS, CustomMaterialTable, DeleteDialog, Loading } from "components";
import { ExportButton } from "components/buttons/exportButton";
import { useNotify } from "hooks";
import { RENEWABLE_ENERGY_PURCHASES_COLUMNS } from "./consts";

export const RenewableEnergyPurchases = () => {
  const userRole = useUserRole();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notify = useNotify();

  const [purchases, setPurchases] = useState<RenewableEnergyPurchasesDTO[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [deletePurchaseModal, setDeletePurchaseModal] =
    useState<boolean>(false);
  const [currentPurchaseId, setCurrentPurchaseId] = useState<number | null>(
    null,
  );

  const { data: renewableEnergyPurchases, isLoading } =
    useGetAllRenewableEnergyPurchases();

  const { mutateAsync: deletePurchaseRequest } = useMutation(deletePurchase);

  const handleEditPurchase = (purchase: RenewableEnergyPurchasesDTO) => {
    navigate("/sites/renewable-energy-purchases-upload", {
      state: { purchase, isEdit: true },
    });
  };

  const handlerOpenPurchaseDeleteModal = async (
    purchase: RenewableEnergyPurchasesDTO,
  ) => {
    const { id } = purchase;
    setCurrentPurchaseId(id || null);
    setDeletePurchaseModal(true);
  };

  const handleDeletePurchase = async (id: number) => {
    try {
      await deletePurchaseRequest(id, {
        onSuccess: () => {
          queryClient.invalidateQueries([
            RENEWABLE_ENERGY_PURCHASES_API_PATHS.RENEWABLE_ENERGY_PURCHASES,
          ]);
          notify.success("Purchase successfully deleted!");
          setDeletePurchaseModal(false);
        },
      });
    } catch (error: any) {
      notify.error(
        error?.response?.data && typeof error?.response?.data === "string"
          ? error?.response?.data
          : "Some error has happened while deleting the purchase!",
      );
    }
  };

  useEffect(() => {
    setPurchases(renewableEnergyPurchases || []);
  }, [renewableEnergyPurchases]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginRight: "24px",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row", gap: 2, mb: 2 }}>
          <Button
            sx={{ height: "36px", width: "240px", borderRadius: 2 }}
            variant="contained"
            onClick={() =>
              navigate("/sites/renewable-energy-purchases-upload", {
                state: { isEdit: false },
              })
            }
            startIcon={<PlusIcon />}
            disabled={userRole.isAuditor}
          >
            Add Renewable Energy
          </Button>
          <ExportButton
            sx={{ height: "36px", width: "200px", borderRadius: 2 }}
            exportFunction={exportRenewableEnergyPurchasesData}
            exportParams={{}}
            exportTitle="renewable_energy_purchases_export"
            exportText="Export to Excel"
            exportingText="Loading..."
            successMessage="Export completed successfully!"
            errorMessage="Failed to Export to Excel"
            isZipFile={false}
            fileExtension=".xlsx"
          />
        </Box>
        <Box
          display="flex"
          gap="2"
          justifyContent="flex-start"
          alignItems="center"
          mb={2}
          sx={{
            width: "240px",
            height: "36px",
            borderRadius: 2,
            border: "1px solid  black",
            marginTop: "7px",
          }}
        >
          <IconButton>
            <EsgSearchIcon />
          </IconButton>
          <TextField
            placeholder="Search sites..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              border: "none",
              "& input": {
                padding: 0,
                fontSize: "14px",
              },
              "& fieldset": {
                border: "none",
              },
            }}
          />
        </Box>
      </Box>
      {isLoading ? (
        <Loading />
      ) : (
        <Box
          sx={{
            position: "relative",
            width: "calc(100vw - 151px)",
            borderRadius: "10px",
          }}
        >
          <CustomMaterialTable
            data={purchases}
            columns={RENEWABLE_ENERGY_PURCHASES_COLUMNS}
            enablePagination={false}
            enableTopToolbar={false}
            enableRowVirtualization={false}
            rowClickable
            muiTableBodyRowProps={({ row }: { row: any }) => ({
              onClick: () => handleEditPurchase(row.original),
            })}
            muiTableContainerProps={{
              sx: {
                height: "calc(100vh - 305px)",
                maxHeight: "calc(100vh - 305px)",
                "& thead > tr:first-of-type > th:first-of-type": {
                  paddingLeft: "30px",
                },
                "& thead tr th": {
                  verticalAlign: "middle",
                },
              },
            }}
            enableRowActions
            renderRowActions={({ row }) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  paddingLeft: "8px",
                }}
              >
                {row.original?.documentUploaded ? (
                  <Tooltip arrow placement="top" title="REP Document">
                    <EsgRecUploaded color="#18AA00" />
                  </Tooltip>
                ) : (
                  <Tooltip arrow placement="top" title="REP Document">
                    <EsgRecUploaded color={COLORS.neutralDarkGray} />
                  </Tooltip>
                )}
                <Tooltip arrow placement="top" title="Delete">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handlerOpenPurchaseDeleteModal(row.original);
                    }}
                  >
                    <EsgDeleteIcon color="red" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          />
        </Box>
      )}
      <DeleteDialog
        isOpen={deletePurchaseModal}
        onCancel={() => setDeletePurchaseModal(false)}
        onDeleteClick={() => handleDeletePurchase(currentPurchaseId!)}
        target="Delete Purchase"
        title="Delete Renewable Energy Purchase?"
      />
    </Box>
  );
};
